import PropTypes from 'prop-types';
import React, { lazy, useMemo, useEffect } from 'react';
import useSWR from 'swr';

import { Navigate } from 'react-router-dom';
import Loader from '../Loader/Loader';
import { get } from '../../util/request';
import { createPaddingProp } from './Page.utils';
import { useBreakpoint } from '../../util/Breakpoint.context';
import { trackEvent as dataLayerPush } from '../../util/track';

const CardContainer = lazy(() => import('../CardContainer/CardContainer'));
const ContentBlock = lazy(() => import('../ContentBlock/ContentBlock'));
const HeaderBlock = lazy(() => import('../HeaderBlock/HeaderBlock'));
const OnePurchasedOneDonated = lazy(() =>
  import('../OnePurchasedOneDonated/OnePurchasedOneDonated')
);
const ResponsiveContainer = lazy(() =>
  import('../ResponsiveContainer/ResponsiveContainer')
);
const ContentfulTextBlock = lazy(() =>
  import('../ContentfulTextBlock/ContentfulTextBlock')
);

const Page = ({ id, endpoint }) => {
  const { data, error } = useSWR(`${endpoint}/v1/pages/${id}`, get, {
    suspense: true
  });

  if (error) {
    window.DD_RUM?.onReady(() => {
      window.DD_RUM?.addError(new Error(error), {
        location: 'Pages',
        type: '404 redirect'
      });
    });

    return <Navigate to="/404" />;
  }
  if (!data) return null;

  useEffect(() => {
    dataLayerPush({ event: 'viewContentPage' });
  }, []);

  const { medium, largest } = useBreakpoint();
  // Run modules through utility functions to add new padding prop and support dynamic removal of top padding
  // run / recompute the value if largest or medium BP value changes
  const formattedModules = useMemo(
    () => createPaddingProp(data.modules, { largest, medium }),
    [medium, largest]
  );

  return (
    <section>
      {formattedModules.map((module) => {
        const blocks = {
          headerBlock: HeaderBlock,
          content_block: ContentBlock,
          textBlock: ContentfulTextBlock,
          cardContainer: CardContainer,
          responsiveContainer: ResponsiveContainer,
          onePurchasedOneDonated: OnePurchasedOneDonated
        };

        const Module = blocks[module.content_type];

        // Once we have API data we can reliably set a key, using id or title for now
        return Module ? (
          <Loader key={module.id || module.title}>
            <Module {...module} />
          </Loader>
        ) : null;
      })}
    </section>
  );
};

Page.propTypes = {
  /**
   * Pages API Endpoint
   */
  endpoint: PropTypes.string.isRequired,
  /**
   * Page ID
   */
  id: PropTypes.string.isRequired
};

export default Page;
